import type { EntityOutboxEntry, EntityOutboxEntityType } from '../db'
import { getOfflineDb } from '../dbFactory'
import { ensureOfflineReady } from '../initOffline'
import {
  ApiError,
  api,
  type FarmCreatePayload,
  type QueuedLotUpsertPayload,
  type QueuedLotMoveCommandPayload,
  type QueuedLotTransferCommandPayload,
  type ProductionUnitCreatePayload,
  type QueuedLotMergeCommandPayload,
  type QueuedLotSplitCommandPayload
} from '../../utils/api'
import { buildFeedLogFingerprint, parseFeedLogFingerprintSource } from '../../utils/feedLogIdentity'
import { normalizeRemoteLotCostEvent } from '../../utils/lotCostEventApi'
import { dexieLotCostEventRepo } from '../repos/DexieLotCostEventRepo'

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const coerceMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  return String(error)
}

const isNotFound = (error: unknown): boolean => (
  error instanceof ApiError && error.status === 404
)

// True when a POST .create() races against a concurrent sync or against a
// direct write that already produced the row on the server. We treat this as
// idempotent success and drop the outbox entry instead of retrying.
//
// Triggers:
//   - HTTP 409 Conflict (any body)
//   - HTTP 400 with Prisma "Unique constraint failed" / "already exists" /
//     "duplicate" in the message
const isAlreadyExists = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false
  if (error.status === 409) return true
  if (error.status === 400) {
    const message = coerceMessage(error).toLowerCase()
    return (
      message.includes('unique constraint') ||
      message.includes('already exists') ||
      message.includes('duplicate')
    )
  }
  return false
}

// Shared "PATCH first, POST on 404" pattern for all upsert-type entities.
// Wraps the POST fallback so that a concurrent writer winning the create race
// (HTTP 409 / 400 unique-constraint) is treated as idempotent success rather
// than a failure that leaves the outbox entry stuck.
const upsertWithFallback = async (
  update: () => Promise<unknown>,
  create: () => Promise<unknown>
): Promise<void> => {
  try {
    await update()
    return
  } catch (error) {
    if (!isNotFound(error)) throw error
  }

  try {
    await create()
  } catch (error) {
    if (isAlreadyExists(error)) return
    throw error
  }
}

const isRetryableNetworkError = (error: unknown): boolean => {
  const message = coerceMessage(error).toLowerCase()
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('fetch')
  )
}

const syncUpsertFarm = async (payload: unknown, entityId: string) => {
  const farm = payload as FarmCreatePayload

  await upsertWithFallback(
    () => api.farms.update(entityId, {
      name: farm.name,
      altitude: farm.altitude,
      location: farm.location,
      metadata: farm.metadata
    }),
    () => api.farms.create({
      id: farm.id ?? entityId,
      name: farm.name,
      altitude: farm.altitude,
      location: farm.location,
      metadata: farm.metadata
    })
  )
}

const syncUpsertProductionUnit = async (payload: unknown, entityId: string) => {
  const unit = payload as ProductionUnitCreatePayload

  await upsertWithFallback(
    () => api.productionUnits.update(entityId, {
      name: unit.name,
      altitude: unit.altitude,
      metadata: unit.metadata
    }),
    () => api.productionUnits.create({
      id: unit.id ?? entityId,
      farmId: unit.farmId,
      name: unit.name,
      altitude: unit.altitude,
      metadata: unit.metadata
    })
  )
}

const syncUpsertWaterLog = async (payload: unknown, entityId: string) => {
  const { lotId: _lotId, ...log } = payload as Record<string, unknown>

  // Charter D-2: WaterLog is production-unit scoped. The server rejects any
  // payload with a `lotId` key (WaterLogService.assertNoLotId → 400), which is
  // a non-retryable error that stalls the entire outbox. Strip it here so an
  // entry queued before the client-side guard (src/utils/waterTable.ts) can't
  // become a permanent poison pill.
  await upsertWithFallback(
    () => api.waterLogs.update(entityId, log),
    () => api.waterLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertProductionRecord = async (payload: unknown, entityId: string) => {
  const record = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.productionRecords.update(entityId, record),
    () => api.productionRecords.create({ ...record, id: entityId })
  )
}

const syncUpsertFeedLog = async (payload: unknown, entityId: string) => {
  const log = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.feedLogs.update(entityId, log),
    () => api.feedLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertMortalityLog = async (payload: unknown, entityId: string) => {
  const log = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.mortalityLogs.update(entityId, log),
    () => api.mortalityLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertWeightSampleLog = async (payload: unknown, entityId: string) => {
  const log = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.weightSampleLogs.update(entityId, log),
    () => api.weightSampleLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertFishStockLog = async (payload: unknown, entityId: string) => {
  const log = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.fishStockLogs.update(entityId, log),
    () => api.fishStockLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertHarvestLog = async (payload: unknown, entityId: string) => {
  const log = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.harvestLogs.update(entityId, log),
    () => api.harvestLogs.create({ ...log, id: entityId })
  )
}

const syncUpsertLotCostEvent = async (payload: unknown, entityId: string) => {
  const event = payload as Record<string, unknown>

  // LotCostEvent's server-side update path forbids the immutable accounting
  // identifiers (categoryPrefix / categorySequence / categoryCode — server
  // returns 400 with a rejectImmutableAccountingKeys guard rather than
  // silently ignoring). The generic upsertWithFallback pattern (PATCH first,
  // POST on 404) fails here because the PATCH returns 400 (not 404) and never
  // falls through. Split the payload: PATCH gets only the mutable fields;
  // POST gets the full shape (server generates the accounting identifiers
  // on create). Session 4 rehearsal 2026-07-07 blocker fix.
  const patchPayload: Record<string, unknown> = {}
  for (const key of [
    'farmId',
    'lotId',
    'productionUnitId',
    'recordedAt',
    'expenseNature',
    'costClassification',
    'amountCents',
    'currency',
    'sourceEventType',
    'sourceEventId',
    'notes',
    'metadata'
  ]) {
    if (key in event) patchPayload[key] = event[key]
  }

  // v2.1 finding #84: capture the server response so the client cache can
  // learn the server-authored accounting identifiers (`categoryPrefix` /
  // `categorySequence` / `categoryCode`) on manual creates. Without this,
  // the optimistic PENDING placeholder that `lotCostEventApi.create`
  // writes to Dexie persists until the operator forces a remote list
  // refresh, and `LotCostSection` renders `pending` next to freshly-saved
  // rows. Auto-derived events (feed → N001) already reach Dexie through
  // the fresh-mount list refresh, so the visible symptom is manual-only.
  let serverResponse: unknown = null
  try {
    serverResponse = await api.lotCostEvents.update(entityId, patchPayload)
  } catch (error) {
    if (!isNotFound(error)) throw error
    try {
      serverResponse = await api.lotCostEvents.create({ ...event, id: entityId })
    } catch (createError) {
      if (isAlreadyExists(createError)) return
      throw createError
    }
  }

  if (serverResponse) {
    const normalized = normalizeRemoteLotCostEvent(serverResponse)
    if (normalized) {
      await dexieLotCostEventRepo.upsert(normalized)
    }
  }
}

const syncUpsertInventoryItem = async (payload: unknown, entityId: string) => {
  const item = payload as Record<string, unknown>

  await upsertWithFallback(
    () => api.inventoryItems.update(entityId, item),
    () => api.inventoryItems.create({ ...item, id: entityId })
  )
}

const syncUpsertTask = async (payload: unknown, entityId: string) => {
  const task = payload as Record<string, unknown>

  const toOptionalString = (key: string): string | undefined => {
    const value = task[key]
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
  }

  const toStringArray = (key: string): string[] => {
    const value = task[key]
    if (!Array.isArray(value)) return []
    return value
      .filter((item) => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const createPayload: Record<string, unknown> = {
    id: entityId,
    title: toOptionalString('title') ?? '',
    collaboratorIds: toStringArray('collaboratorIds'),
    reminderSettings: typeof task.reminderSettings === 'object' && task.reminderSettings !== null
      ? task.reminderSettings
      : undefined,
    tags: toStringArray('tags'),
    metadata: typeof task.metadata === 'object' && task.metadata !== null ? task.metadata : undefined
  }

  const description = toOptionalString('description')
  if (description) createPayload.description = description

  const assigneeId = toOptionalString('assigneeId')
  if (assigneeId) createPayload.assigneeId = assigneeId

  const dueAt = toOptionalString('dueAt')
  if (dueAt) createPayload.dueAt = dueAt

  const parentId = toOptionalString('parentId')
  if (parentId) createPayload.parentId = parentId

  if (typeof task.completed === 'boolean' && task.completed) {
    createPayload.completed = true
  }

  const completedAt = toOptionalString('completedAt')
  if (completedAt) createPayload.completedAt = completedAt

  // Update accepts nullable values; for upsert we want full-state semantics.
  const updatePayload: Record<string, unknown> = {
    title: typeof task.title === 'string' ? task.title : undefined,
    description: typeof task.description === 'string' ? task.description : null,
    assigneeId: typeof task.assigneeId === 'string' ? task.assigneeId : null,
    collaboratorIds: toStringArray('collaboratorIds'),
    dueAt: typeof task.dueAt === 'string' ? task.dueAt : null,
    parentId: typeof task.parentId === 'string' ? task.parentId : null,
    reminderSettings: typeof task.reminderSettings === 'object' && task.reminderSettings !== null
      ? task.reminderSettings
      : null,
    metadata: typeof task.metadata === 'object' && task.metadata !== null ? task.metadata : null,
    tags: toStringArray('tags'),
    completed: typeof task.completed === 'boolean' ? task.completed : undefined,
    completedAt: typeof task.completedAt === 'string' ? task.completedAt : null
  }

  await upsertWithFallback(
    () => api.tasks.update(entityId, updatePayload as any),
    () => api.tasks.create(createPayload as any)
  )
}

const syncUpsertSfrMatrix = async (payload: unknown, entityId: string) => {
  const record = payload as Record<string, unknown>
  const species = typeof record.species === 'string' && record.species.trim() ? record.species.trim() : entityId
  const matrix = record.matrix

  // PUT is idempotent; no update/create distinction.
  await api.sfrMatrix.save({
    species,
    matrix: matrix as any
  })
}

const syncUpsertMortalityMatrix = async (payload: unknown, entityId: string) => {
  const record = payload as Record<string, unknown>
  const species = typeof record.species === 'string' && record.species.trim() ? record.species.trim() : entityId
  const matrix = record.matrix

  await api.mortalityMatrix.save({
    species,
    matrix: matrix as any
  })
}

const syncUpsertLot = async (payload: unknown, entityId: string) => {
  const lot = payload as QueuedLotUpsertPayload
  const eventId = lot.localLedgerEvent?.id

  const { localLedgerEvent: _localLedgerEvent, ...createPayload } = lot

  await upsertWithFallback(
    () => api.lots.update(entityId, {
      lotKey: lot.lotKey,
      lineageRootId: lot.lineageRootId,
      sourceLotKey: lot.sourceLotKey,
      farmId: lot.farmId,
      productionUnitId: lot.productionUnitId,
      locationType: lot.locationType,
      locationId: lot.locationId,
      species: lot.species,
      stage: lot.stage,
      stockingDate: lot.stockingDate,
      harvestDate: lot.harvestDate,
      biomassKg: lot.biomassKg,
      metadata: lot.metadata,
      eventId
    }),
    () => api.lots.create({
      ...createPayload,
      id: lot.id ?? entityId,
      eventId
    })
  )
}

const syncLotSplitCommand = async (payload: unknown) => {
  const command = payload as QueuedLotSplitCommandPayload
  await api.lots.split(command.sourceLotId, {
    childLotKeys: command.childLotKeys,
    childLotIds: command.childLotIds,
    eventId: command.eventId,
    recordedAt: command.recordedAt,
    notes: command.notes
  })
}

const syncLotMoveCommand = async (payload: unknown) => {
  const command = payload as QueuedLotMoveCommandPayload
  await api.lots.move(command.sourceLotId, {
    targetProductionUnitId: command.targetProductionUnitId,
    targetLocationType: command.targetLocationType,
    targetLocationId: command.targetLocationId,
    eventId: command.eventId,
    recordedAt: command.recordedAt,
    notes: command.notes
  })
}

const syncLotTransferCommand = async (payload: unknown) => {
  const command = payload as QueuedLotTransferCommandPayload
  await api.lots.transfer(command.sourceLotId, {
    targetFarmId: command.targetFarmId,
    targetProductionUnitId: command.targetProductionUnitId,
    targetLocationType: command.targetLocationType,
    targetLocationId: command.targetLocationId,
    eventId: command.eventId,
    recordedAt: command.recordedAt,
    notes: command.notes
  })
}

const syncLotMergeCommand = async (payload: unknown) => {
  const command = payload as QueuedLotMergeCommandPayload
  await api.lots.merge({
    sourceLotIds: command.sourceLotIds,
    targetLotKey: command.targetLotKey,
    targetLotId: command.targetLotId,
    eventId: command.eventId,
    recordedAt: command.recordedAt,
    notes: command.notes,
    markSourceLotsInactive: command.markSourceLotsInactive
  })
}

const resolveFeedLogDeleteTargetId = async (payload: unknown): Promise<string | null> => {
  const snapshot = parseFeedLogFingerprintSource(payload)
  if (!snapshot) return null

  const remote = await api.feedLogs.list({
    farmId: snapshot.farmId,
    productionUnitId: snapshot.productionUnitId,
    lot: snapshot.lot,
    fromRecordedAt: snapshot.recordedAt,
    toRecordedAt: snapshot.recordedAt,
    noCache: true
  })

  const targetFingerprint = buildFeedLogFingerprint(snapshot)
  const match = remote.find((log) => buildFeedLogFingerprint(log) === targetFingerprint)
  return match?.id ?? null
}

// Findings #66: the four capture-log DELETE routes require a reason. Deletes
// queued before this release carry none; they still sync, labelled as such.
export const LEGACY_QUEUED_DELETE_REASON = 'Eliminado antes de que se exigiera un motivo (cola sin sincronizar)'

const syncDelete = async (
  entityType: EntityOutboxEntityType,
  entityId: string,
  payload?: unknown,
  reason?: string,
  operatorActorId?: string
) => {
  const audit = {
    reason: reason?.trim() || LEGACY_QUEUED_DELETE_REASON,
    ...(operatorActorId ? { operatorActorId } : {})
  }
  if (entityType === 'feedLog') {
    try {
      await api.feedLogs.delete(entityId, audit)
      return
    } catch (error) {
      if (!isNotFound(error)) throw error

      const fallbackId = await resolveFeedLogDeleteTargetId(payload)
      if (!fallbackId || fallbackId === entityId) return

      try {
        await api.feedLogs.delete(fallbackId, audit)
      } catch (fallbackError) {
        if (!isNotFound(fallbackError)) throw fallbackError
      }
      return
    }
  }

  try {
    if (entityType === 'farm') {
      await api.farms.delete(entityId)
      return
    }
    if (entityType === 'productionUnit') {
      await api.productionUnits.delete(entityId)
      return
    }
    if (entityType === 'lot') {
      await api.lots.delete(entityId)
      return
    }
    if (entityType === 'waterLog') {
      await api.waterLogs.delete(entityId, audit)
      return
    }
    if (entityType === 'productionRecord') {
      await api.productionRecords.delete(entityId)
      return
    }
    if (entityType === 'mortalityLog') {
      await api.mortalityLogs.delete(entityId, audit)
      return
    }
    if (entityType === 'weightSampleLog') {
      await api.weightSampleLogs.delete(entityId, audit)
      return
    }
    if (entityType === 'fishStockLog') {
      await api.fishStockLogs.delete(entityId)
      return
    }
    if (entityType === 'harvestLog') {
      await api.harvestLogs.delete(entityId)
      return
    }
    if (entityType === 'lotCostEvent') {
      await api.lotCostEvents.delete(entityId)
      return
    }
    if (entityType === 'inventoryItem') {
      await api.inventoryItems.delete(entityId)
      return
    }
    if (entityType === 'task') {
      await api.tasks.delete(entityId)
      return
    }
    // SFR matrices are shared configs; delete is not supported.
  } catch (error) {
    if (isNotFound(error)) return
    throw error
  }
}

const syncEntry = async (entry: EntityOutboxEntry) => {
  if (entry.op === 'delete') {
    await syncDelete(entry.entityType, entry.entityId, entry.payload, entry.reason, entry.operatorActorId)
    return
  }

  if (entry.entityType === 'farm') {
    await syncUpsertFarm(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'productionUnit') {
    await syncUpsertProductionUnit(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'lot') {
    await syncUpsertLot(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'lotMoveCommand') {
    await syncLotMoveCommand(entry.payload)
    return
  }

  if (entry.entityType === 'lotTransferCommand') {
    await syncLotTransferCommand(entry.payload)
    return
  }

  if (entry.entityType === 'lotSplitCommand') {
    await syncLotSplitCommand(entry.payload)
    return
  }

  if (entry.entityType === 'lotMergeCommand') {
    await syncLotMergeCommand(entry.payload)
    return
  }

  if (entry.entityType === 'waterLog') {
    await syncUpsertWaterLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'productionRecord') {
    await syncUpsertProductionRecord(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'feedLog') {
    await syncUpsertFeedLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'mortalityLog') {
    await syncUpsertMortalityLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'weightSampleLog') {
    await syncUpsertWeightSampleLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'fishStockLog') {
    await syncUpsertFishStockLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'harvestLog') {
    await syncUpsertHarvestLog(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'lotCostEvent') {
    await syncUpsertLotCostEvent(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'inventoryItem') {
    await syncUpsertInventoryItem(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'task') {
    await syncUpsertTask(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'mortalityMatrix') {
    await syncUpsertMortalityMatrix(entry.payload, entry.entityId)
    return
  }

  if (entry.entityType === 'sfrMatrix') {
    await syncUpsertSfrMatrix(entry.payload, entry.entityId)
  }
}

const shouldDropNonRetriableNotFound = (entry: EntityOutboxEntry, error: unknown): boolean => {
  if (!(error instanceof ApiError) || error.status !== 404) return false
  return entry.entityType === 'task'
}

// Single-flight guard. Multiple concurrent triggers (online event, focus event,
// drainEntityOutboxNow, explicit user retry) used to race each other on the
// same outbox entries: each call would PATCH the same id, fall back to POST,
// and the second POST would fail with `400 Unique constraint failed on (id)`.
// We coalesce concurrent callers to the in-flight result.
let activeSyncRun: Promise<{
  processed: number
  remaining: number
  skippedNotFound: number
}> | null = null

const runSyncEntityOutboxNow = async (params?: { maxEntries?: number }): Promise<{
  processed: number
  remaining: number
  skippedNotFound: number
}> => {
  await ensureOfflineReady()

  const offlineDb = getOfflineDb()

  if (!isOnline()) {
    const remaining = await offlineDb.entityOutbox.count()
    return { processed: 0, remaining, skippedNotFound: 0 }
  }

  const maxEntries = Math.max(1, Math.min(params?.maxEntries ?? 25, 250))

  let processed = 0
  let skippedNotFound = 0

  const entries = await offlineDb.entityOutbox.orderBy('seq').limit(maxEntries).toArray()

  for (const entry of entries) {
    try {
      await syncEntry(entry)
      if (typeof entry.seq === 'number') {
        await offlineDb.entityOutbox.delete(entry.seq)
      }
      processed += 1
    } catch (error) {
      if (shouldDropNonRetriableNotFound(entry, error)) {
        if (typeof entry.seq === 'number') {
          await offlineDb.entityOutbox.delete(entry.seq)
        }
        processed += 1
        skippedNotFound += 1
        continue
      }

      const attemptCount = (entry.attemptCount ?? 0) + 1
      if (typeof entry.seq === 'number') {
        await offlineDb.entityOutbox.update(entry.seq, {
          attemptCount,
          lastError: coerceMessage(error),
          lastErrorStatus: error instanceof ApiError ? error.status : undefined
        })
      }

      // Stop on network/offline-ish errors to avoid tight loops.
      if (isRetryableNetworkError(error)) {
        break
      }

      // Also stop on non-404 API errors so we don't churn.
      break
    }
  }

  const remaining = await offlineDb.entityOutbox.count()
  return { processed, remaining, skippedNotFound }
}

export const syncEntityOutboxNow = async (params?: { maxEntries?: number }): Promise<{
  processed: number
  remaining: number
  skippedNotFound: number
}> => {
  // If a sync is already in flight, return its result so concurrent callers
  // (online event + focus event + manual retry) don't race PATCH/POST pairs
  // on the same outbox entry.
  if (activeSyncRun) {
    return activeSyncRun
  }

  const run = (async () => {
    try {
      return await runSyncEntityOutboxNow(params)
    } finally {
      activeSyncRun = null
    }
  })()
  activeSyncRun = run
  return run
}

export const drainEntityOutboxNow = async (params?: {
  maxEntries?: number
  maxPasses?: number
}): Promise<{ processed: number; remaining: number; passes: number; skippedNotFound: number }> => {
  const maxPasses = Math.max(1, Math.min(params?.maxPasses ?? 5, 20))

  let processed = 0
  let remaining = 0
  let passes = 0
  let skippedNotFound = 0

  for (let index = 0; index < maxPasses; index += 1) {
    const result = await syncEntityOutboxNow({ maxEntries: params?.maxEntries })
    processed += result.processed
    remaining = result.remaining
    skippedNotFound += result.skippedNotFound
    passes += 1

    if (result.remaining <= 0 || result.processed <= 0) {
      break
    }
  }

  return { processed, remaining, passes, skippedNotFound }
}
